import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asset } from "@/lib/api";
import { Clock, GraduationCap, Award, Users, Check, PlayCircle, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CourseDetail() {
  const { courseId } = useParams();
  const [c, setC] = useState(null);
  const [progress, setProgress] = useState({});
  const { user, login } = useAuth();

  useEffect(() => {
    api.get(`/courses/${courseId}`).then(r => setC(r.data)).catch(() => setC(null));
    if (user) {
      api.get("/me/progress").then(r => {
        const map = {};
        r.data.forEach(p => { if (p.course_id === courseId) map[p.lesson_index] = p.completed; });
        setProgress(map);
      }).catch(() => {});
    }
  }, [courseId, user]);

  if (!c) return <div className="container-x py-32 text-center text-muted-foreground">Loading…</div>;

  const completed = Object.values(progress).filter(Boolean).length;
  const pct = c.lessons.length ? Math.round((completed / c.lessons.length) * 100) : 0;

  const toggle = async (idx, done) => {
    if (!user) { login(); return; }
    setProgress(p => ({ ...p, [idx]: done }));
    try { await api.post("/me/progress", { course_id: c.id, lesson_index: idx, completed: done }); }
    catch { toast.error("Couldn't save progress"); }
  };

  const enroll = async () => {
    if (!user) { login(); return; }
    try { await api.post(`/me/enroll/${c.id}`); toast.success("Enrolled. See you in the Village."); }
    catch { toast.error("Something went wrong"); }
  };

  return (
    <div>
      <section className="relative min-h-[70vh] w-full overflow-hidden flex items-end">
        <img src={asset(c.cover)} alt={c.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/10" />
        <div className="relative container-x pb-16 pt-32">
          <div className="flex items-center gap-2 eyebrow text-foreground/80">
            <Link to="/learn" className="hover:text-foreground">Learning Center</Link>
            <ChevronRight className="h-3 w-3" />
            <span>{c.track}</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.98] mt-4 max-w-4xl text-balance">{c.title}</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{c.tagline}</p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2"><GraduationCap className="h-4 w-4" /> {c.level}</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {c.duration}</span>
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> {c.instructor}</span>
            <span className="inline-flex items-center gap-2"><Award className="h-4 w-4" /> Certificate on completion</span>
          </div>
        </div>
      </section>

      <section className="container-x py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="eyebrow">About this course</div>
          <p className="mt-4 text-lg leading-relaxed">{c.description}</p>

          <div className="mt-12 eyebrow">What you'll learn</div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {c.learning_outcomes.map((lo) => (
              <li key={lo} className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center">
                  <Check className="h-3 w-3" />
                </span>
                <span className="text-sm">{lo}</span>
              </li>
            ))}
          </ul>

          <div className="mt-14 eyebrow">Syllabus</div>
          <Accordion type="single" collapsible className="mt-4">
            {c.lessons.map((l, i) => (
              <AccordionItem value={`l-${i}`} key={i} data-testid={`lesson-${i}`}>
                <AccordionTrigger>
                  <div className="flex items-center gap-3 text-left">
                    <span className="font-display text-lg text-muted-foreground w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-base">{l.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-3"><PlayCircle className="h-4 w-4" /> {l.duration}</div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!progress[i]}
                        onChange={e => toggle(i, e.target.checked)}
                        data-testid={`lesson-check-${i}`}
                      /> Mark complete
                    </label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 rounded-lg border border-border bg-card p-6">
            <div className="eyebrow">Your progress</div>
            <div className="mt-3 flex items-baseline justify-between">
              <div className="font-display text-4xl">{pct}%</div>
              <div className="text-xs text-muted-foreground">{completed} / {c.lessons.length} lessons</div>
            </div>
            <Progress value={pct} className="mt-3" />

            <button onClick={enroll} className="btn-primary w-full mt-6" data-testid="course-enroll-btn">
              {user ? "Enroll in course" : "Sign in to enroll"}
            </button>
            <div className="mt-4 text-xs text-muted-foreground">
              Completing this course adds to your Certification track and unlocks community badges.
            </div>

            <div className="hairline my-6" />
            <div className="eyebrow">Instructor</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-secondary grid place-items-center font-display text-lg">
                {c.instructor?.[0]}
              </div>
              <div>
                <div className="font-semibold">{c.instructor}</div>
                <div className="text-xs text-muted-foreground">Wolfdog Village Faculty</div>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
